// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Choose project" for a new agent chat (design/agents-project-picker, picked E + F). On
// desktop a fixed-size dialog with two panes: All machines first, then each machine (offline ones
// greyed); on the right Recent across machines and a group per machine, or one machine's projects,
// newest first. Arrow keys move, Enter picks, ←/→ switch machine. Add project and Manage projects
// open in the same box. Phones and the Send to an agent panel get the same list under a chip row.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {MachineGlyph, OnlineDot} from '@app/features/konvo_agents/components/AgentBrand';
import {AgentProjectAdd, LoadingRows} from '@app/features/konvo_agents/components/AgentProjectAdd';
import {AgentProjectManage} from '@app/features/konvo_agents/components/AgentProjectManage';
import styles from '@app/features/konvo_agents/components/AgentProjectPicker.module.css';
import {projectLastUsed} from '@app/features/konvo_agents/components/AgentsCopy';
import {homeOf} from '@app/features/konvo_agents/state/AgentProjects';
import {
	canManageProjects,
	type ProjectUse,
	pickerMachines,
	projectUse,
	recentProjects,
	shortPath,
	sortProjects,
	tildePath,
} from '@app/features/konvo_agents/state/AgentProjectsModel';
import Agents from '@app/features/konvo_agents/state/Agents';
import {revealInList} from '@app/features/konvo_agents/utils/AgentListScroll';
import {BottomSheet} from '@app/features/ui/bottom_sheet/BottomSheet';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import Users from '@app/features/user/state/Users';
import type {AgentMachineView, AgentProject} from '@konvo/schema/src/domains/konvo/Agents';
import {plural} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	IconAdjustmentsHorizontal,
	IconArrowDown,
	IconArrowLeft,
	IconArrowRight,
	IconArrowUp,
	IconCheck,
	IconChevronLeft,
	IconClock,
	IconCornerDownLeft,
	IconDevices,
	IconFolder,
	IconPlus,
	IconSearch,
	IconX,
} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';

export {shortPath};

const MODAL_KEY = 'konvo-agents-choose-project';
const ALL = 'all';

/** Projects archived in T3 keep their chats but aren't offered for new ones, as in T3. */
const openProjectsOf = (machineId: string) => Agents.projectsOf(machineId).filter((project) => !project.archived);

export interface ProjectChoice {
	machineId: string;
	projectId: string;
}

export type ProjectView = 'choose' | 'add' | 'manage';
type Layout = 'panes' | 'chips';

interface Row {
	key: string;
	machine: AgentMachineView;
	project: AgentProject;
	use: ProjectUse | undefined;
	/** Recent rows name their machine. */
	tagged: boolean;
}

interface Section {
	key: string;
	kind: 'recent' | 'machine';
	machine?: AgentMachineView;
	rows: Array<Row>;
	/** Recent while a machine's chats are still coming in (they say which projects were used last). */
	loading?: boolean;
}

/** The machine pane (desktop): All, each machine, then Add and Manage. */
const MachinePane = observer(
	({
		pane,
		onPane,
		allowAll,
		view,
		onView,
		counts,
	}: {
		pane: string;
		onPane: (pane: string) => void;
		allowAll: boolean;
		view: ProjectView;
		onView: (view: ProjectView) => void;
		/** Match counts per machine while searching. */
		counts: Map<string, number> | null;
	}) => {
		const {t} = useLingui();
		const machines = pickerMachines(Agents.machines);
		const projectCount = machines.reduce((n, m) => n + openProjectsOf(m.id).length, 0);
		const online = machines.filter((m) => m.online).length;
		const allCount = counts ? [...counts.values()].reduce((a, b) => a + b, 0) : null;
		return (
			<nav className={styles.side} aria-label={t`Machines`} data-flx="konvo-agents.choose-project.machines">
				<button
					type="button"
					className={clsx(styles.machine, pane === ALL && styles.machineOn, !allowAll && styles.machineMuted)}
					onClick={() => onPane(ALL)}
					disabled={!allowAll}
					aria-current={pane === ALL || undefined}
					data-flx="konvo-agents.choose-project.all"
				>
					<span className={styles.osTile}>
						<IconDevices size={20} stroke={1.6} />
					</span>
					<span className={styles.machineText}>
						<strong>{t`All machines`}</strong>
						<span>
							{allCount != null
								? plural(allCount, {one: '# match', other: '# matches'})
								: `${plural(projectCount, {one: '# project', other: '# projects'})} · ${t`${online} online`}`}
						</span>
					</span>
				</button>
				<div className={styles.sideLabel}>{t`Machines`}</div>
				{machines.map((machine) => {
					const matchCount = counts?.get(machine.id) ?? null;
					const machineProjects = openProjectsOf(machine.id).length;
					return (
						<button
							key={machine.id}
							type="button"
							className={clsx(
								styles.machine,
								pane === machine.id && styles.machineOn,
								(!machine.online || matchCount === 0) && styles.machineMuted,
							)}
							onClick={() => onPane(machine.id)}
							aria-current={pane === machine.id || undefined}
							data-flx="konvo-agents.choose-project.machine"
						>
							<span className={styles.osTile}>
								<MachineGlyph platform={machine.platform} size={20} />
								<OnlineDot online={machine.online} className={styles.osDot} />
							</span>
							<span className={styles.machineText}>
								<strong>{machine.name}</strong>
								<span>
									{matchCount != null
										? plural(matchCount, {one: '# match', other: '# matches'})
										: machine.online
											? plural(machineProjects, {one: '# project', other: '# projects'})
											: t`Offline`}
								</span>
							</span>
						</button>
					);
				})}
				<span className={styles.sideSpacer} />
				<button
					type="button"
					className={clsx(styles.sideAction, styles.sideActionStrong, view === 'add' && styles.sideActionOn)}
					onClick={() => onView('add')}
					data-flx="konvo-agents.choose-project.add"
				>
					<IconPlus size={16} stroke={1.8} />
					{t`Add project`}
				</button>
				<button
					type="button"
					className={clsx(styles.sideAction, view === 'manage' && styles.sideActionOn)}
					onClick={() => onView('manage')}
					data-flx="konvo-agents.choose-project.manage"
				>
					<IconAdjustmentsHorizontal size={16} stroke={1.7} />
					{t`Manage projects`}
				</button>
			</nav>
		);
	},
);

/** The machines as a chip row (phones and panels), with Add and Manage at the end. */
const MachineChips = observer(
	({
		pane,
		onPane,
		allowAll,
		onView,
		view,
	}: {
		pane: string;
		onPane: (pane: string) => void;
		allowAll: boolean;
		view: ProjectView;
		onView: (view: ProjectView) => void;
	}) => {
		const {t} = useLingui();
		const machines = pickerMachines(Agents.machines);
		return (
			<div className={styles.chips} data-flx="konvo-agents.choose-project.chips">
				{allowAll && (
					<button
						type="button"
						className={clsx(styles.chip, pane === ALL && styles.chipOn)}
						onClick={() => onPane(ALL)}
						aria-pressed={pane === ALL}
					>
						<IconDevices size={15} stroke={1.6} />
						{t`All`}
					</button>
				)}
				{machines.map((machine) => (
					<button
						key={machine.id}
						type="button"
						className={clsx(styles.chip, pane === machine.id && styles.chipOn, !machine.online && styles.chipMuted)}
						onClick={() => onPane(machine.id)}
						aria-pressed={pane === machine.id}
					>
						<MachineGlyph platform={machine.platform} size={15} />
						{machine.name}
					</button>
				))}
				{view === 'choose' && (
					<>
						<Tooltip text={t`Add project`} position="bottom">
							<button
								type="button"
								className={clsx(styles.chip, styles.chipIcon)}
								onClick={() => onView('add')}
								aria-label={t`Add project`}
								data-flx="konvo-agents.choose-project.add"
							>
								<IconPlus size={17} stroke={1.8} />
							</button>
						</Tooltip>
						<Tooltip text={t`Manage projects`} position="bottom">
							<button
								type="button"
								className={clsx(styles.chip, styles.chipIcon)}
								onClick={() => onView('manage')}
								aria-label={t`Manage projects`}
								data-flx="konvo-agents.choose-project.manage"
							>
								<IconAdjustmentsHorizontal size={17} stroke={1.7} />
							</button>
						</Tooltip>
					</>
				)}
			</div>
		);
	},
);

/**
 * Choose, add and manage projects. `panes` is the desktop dialog's body; `chips` fits a phone sheet
 * or the Send to an agent panel.
 */
export const AgentProjectBrowser = observer(
	({
		layout,
		value,
		onPick,
		autoFocus = false,
		initialView = 'choose',
		onClose,
		onViewChange,
	}: {
		layout: Layout;
		value?: ProjectChoice | null;
		onPick: (choice: ProjectChoice) => void;
		autoFocus?: boolean;
		initialView?: ProjectView;
		onClose?: () => void;
		onViewChange?: (view: ProjectView) => void;
	}) => {
		const {t, i18n} = useLingui();
		const [view, setViewState] = useState<ProjectView>(initialView);
		const [pane, setPane] = useState<string>(ALL);
		const [query, setQuery] = useState('');
		const [active, setActive] = useState(0);
		const listRef = useRef<HTMLDivElement | null>(null);
		const searchRef = useRef<HTMLInputElement | null>(null);
		const now = Agents.minute;
		const machines = pickerMachines(Agents.machines);

		useEffect(() => {
			if (Agents.loaded) Agents.ensureLists();
			else void Agents.loadOverview().then(() => Agents.ensureLists());
		}, []);
		const listsLoading = machines.some((m) => {
			const list = Agents.list(m.id);
			return m.online && (!list || (list.loading && list.threads == null));
		});

		const setView = (next: ProjectView) => {
			// Add and Manage work on one machine: the one in view, else the chosen project's, else the first online one.
			if (next !== 'choose' && pane === ALL) {
				const preferred =
					machines.find((m) => m.id === value?.machineId && m.online) ?? machines.find((m) => m.online) ?? machines[0];
				if (preferred) setPane(preferred.id);
			}
			setViewState(next);
			onViewChange?.(next);
		};

		const needle = query.trim().toLocaleLowerCase();
		const matches = (project: AgentProject) =>
			!needle || `${project.title} ${project.path ?? ''}`.toLocaleLowerCase().includes(needle);

		const perMachine = machines.map((machine) => {
			const uses = projectUse(Agents.threadsOf(machine.id), now);
			return {machine, uses, projects: sortProjects(openProjectsOf(machine.id), uses)};
		});

		const sections: Array<Section> = [];
		if (pane === ALL) {
			if (!needle) {
				const recent = recentProjects(perMachine);
				if (recent.length > 1 || machines.length > 1 || listsLoading)
					sections.push({
						key: 'recent',
						kind: 'recent',
						loading: listsLoading,
						rows: recent.map(({machine, project, use}) => ({
							key: `recent:${machine.id}:${project.id}`,
							machine,
							project,
							use,
							tagged: true,
						})),
					});
			}
			for (const {machine, uses, projects} of perMachine) {
				const shown = projects.filter(matches);
				if (shown.length)
					sections.push({
						key: machine.id,
						kind: 'machine',
						machine,
						rows: shown.map((project) => ({
							key: `${machine.id}:${project.id}`,
							machine,
							project,
							use: uses.get(project.id),
							tagged: false,
						})),
					});
			}
		} else {
			const entry = perMachine.find((e) => e.machine.id === pane);
			if (entry)
				sections.push({
					key: entry.machine.id,
					kind: 'machine',
					machine: entry.machine,
					rows: entry.projects.filter(matches).map((project) => ({
						key: `${entry.machine.id}:${project.id}`,
						machine: entry.machine,
						project,
						use: entry.uses.get(project.id),
						tagged: false,
					})),
				});
		}
		const rows = sections.flatMap((s) => s.rows);
		const counts = needle
			? new Map(perMachine.map(({machine, projects}) => [machine.id, projects.filter(matches).length]))
			: null;

		// Start on the chosen project; keep the highlight on the list as it changes.
		const firstPlaced = useRef(false);
		useEffect(() => {
			if (!firstPlaced.current && value && rows.length) {
				const at = rows.findIndex((r) => r.machine.id === value.machineId && r.project.id === value.projectId);
				if (at >= 0) setActive(at);
				firstPlaced.current = true;
			}
		}, [rows.length, value]);
		useEffect(() => setActive(0), [needle, pane]);
		const current = rows[Math.min(active, Math.max(rows.length - 1, 0))];

		useEffect(() => {
			revealInList(listRef.current, listRef.current?.querySelector<HTMLElement>(`[data-row-index="${active}"]`));
		}, [active]);

		const pick = (row: Row | undefined) => {
			if (!row || !row.machine.online) return;
			onPick({machineId: row.machine.id, projectId: row.project.id});
		};

		const paneOrder = [ALL, ...machines.map((m) => m.id)];
		const onKeyDown = (event: React.KeyboardEvent) => {
			if (view !== 'choose') return;
			if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
				event.preventDefault();
				if (!rows.length) return;
				const step = event.key === 'ArrowDown' ? 1 : -1;
				setActive((at) => (Math.min(at, rows.length - 1) + step + rows.length) % rows.length);
			} else if (event.key === 'Enter') {
				event.preventDefault();
				pick(current);
			} else if (
				layout === 'panes' &&
				(event.key === 'ArrowLeft' || event.key === 'ArrowRight') &&
				!(event.target instanceof HTMLInputElement && event.target.value)
			) {
				event.preventDefault();
				const at = paneOrder.indexOf(pane);
				const next = paneOrder[(at + (event.key === 'ArrowRight' ? 1 : -1) + paneOrder.length) % paneOrder.length];
				setPane(next);
			}
		};

		const paneMachine = pane === ALL ? null : (machines.find((m) => m.id === pane) ?? null);
		const paneMachineName = paneMachine?.name ?? '';
		const paneProjectCount = paneMachine ? openProjectsOf(paneMachine.id).length : 0;
		const me = Users.currentUser?.displayName ?? '';
		const startsOn = current?.machine.name ?? paneMachine?.name ?? '';
		let rowIndex = -1;

		const list = (
			<div className={styles.scroll} ref={listRef} data-flx="konvo-agents.choose-project.list">
				{machines.length === 0 &&
					(Agents.loaded ? (
						<p className={styles.empty}>{t`None of your machines allows starting chats from Konvo.`}</p>
					) : (
						<LoadingRows />
					))}
				{machines.length > 0 && rows.length === 0 && !listsLoading && (
					<p className={styles.empty}>
						{needle
							? t`No projects match.`
							: paneMachine && !paneMachine.online
								? t`${paneMachineName} is offline.`
								: t`No projects yet. Add one to start a chat in it.`}
					</p>
				)}
				{sections.map((section) => {
					const body = section.rows.map((row) => {
						rowIndex++;
						const index = rowIndex;
						const chosen = value?.machineId === row.machine.id && value.projectId === row.project.id;
						const path = tildePath(row.project.path, homeOf(row.machine.id));
						const when = projectLastUsed(i18n, row.use, now);
						return layout === 'panes' ? (
							<button
								key={row.key}
								type="button"
								className={clsx(styles.row, index === active && styles.rowActive)}
								onClick={() => pick(row)}
								onMouseMove={() => index !== active && setActive(index)}
								disabled={!row.machine.online}
								data-row-index={index}
								aria-current={chosen || undefined}
								data-flx="konvo-agents.choose-project.row"
							>
								<IconFolder size={18} stroke={1.6} className={styles.folder} />
								<span className={styles.rowTitle}>{row.project.title}</span>
								<span className={styles.rowPath} title={row.project.path}>
									{path}
								</span>
								{row.tagged && (
									<span className={styles.tag}>
										<MachineGlyph platform={row.machine.platform} size={13} />
										{row.machine.name}
									</span>
								)}
								<span className={styles.when}>{when}</span>
								<span className={styles.check}>{chosen && <IconCheck size={16} stroke={2} />}</span>
							</button>
						) : (
							<button
								key={row.key}
								type="button"
								className={clsx(styles.row, chosen && styles.rowActive)}
								onClick={() => pick(row)}
								disabled={!row.machine.online}
								data-row-index={index}
								aria-current={chosen || undefined}
								data-flx="konvo-agents.choose-project.row"
							>
								<IconFolder size={20} stroke={1.6} className={styles.folder} />
								<span className={styles.rowStack}>
									<span className={styles.rowTitle}>{row.project.title}</span>
									<span className={styles.rowPath} title={row.project.path}>
										{row.tagged ? `${row.machine.name} · ${shortPath(path, 36)}` : shortPath(path, 40)}
									</span>
								</span>
								<span className={styles.when}>{when}</span>
							</button>
						);
					});
					const label =
						section.kind === 'recent' ? (
							<div className={styles.label}>
								<IconClock size={14} stroke={1.7} />
								{t`Recent`}
								{section.loading && <Spinner size="small" className={styles.labelSpinner} />}
							</div>
						) : pane === ALL || layout === 'chips' ? (
							<div className={clsx(styles.label, !section.machine!.online && styles.labelMuted)}>
								<MachineGlyph platform={section.machine!.platform} size={14} />
								{section.machine!.name}
								<OnlineDot online={section.machine!.online} />
								<span className={styles.labelSub}>
									{section.machine!.online ? section.machine!.tools.map((tool) => tool.name).join(', ') : t`Offline`}
								</span>
							</div>
						) : null;
					return (
						<section key={section.key}>
							{label}
							{section.loading && body.length === 0 ? (
								<LoadingRows count={layout === 'chips' ? 2 : 3} />
							) : layout === 'chips' ? (
								<div className={styles.card}>{body}</div>
							) : (
								body
							)}
						</section>
					);
				})}
			</div>
		);

		const searchBox = (placeholder: string) => (
			<label className={styles.search}>
				<IconSearch size={17} stroke={1.7} />
				<input
					ref={searchRef}
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					placeholder={placeholder}
					aria-label={placeholder}
					// biome-ignore lint/a11y/noAutofocus: the picker opens to choose, often by typing.
					autoFocus={autoFocus}
					data-flx="konvo-agents.choose-project.search"
				/>
			</label>
		);

		const searchLabel = paneMachine ? t`Search projects on ${paneMachineName}` : t`Search all projects`;
		const managed = paneMachine ?? machines.find((m) => m.online) ?? null;
		const managedName = managed?.name ?? '';
		const back = () => setView('choose');

		const sideView =
			view === 'add' && managed ? (
				<AgentProjectAdd
					key={managed.id}
					machine={managed}
					layout={layout}
					onCancel={back}
					onAdded={(added, start) => {
						if (start) onPick({machineId: managed.id, projectId: added.projectId});
						else back();
					}}
				/>
			) : view === 'manage' && managed ? (
				<AgentProjectManage key={managed.id} machine={managed} layout={layout} onAdd={() => setView('add')} />
			) : null;

		if (layout === 'chips') {
			return (
				// biome-ignore lint/a11y/noStaticElementInteractions: picker-wide arrow keys and Enter; every row is a button.
				<div
					className={clsx(styles.browser, styles.chipsLayout)}
					onKeyDown={onKeyDown}
					data-flx="konvo-agents.choose-project"
				>
					{view !== 'choose' && (
						<button
							type="button"
							className={styles.sideAction}
							onClick={back}
							data-flx="konvo-agents.choose-project.back"
						>
							<IconChevronLeft size={18} stroke={1.8} />
							{t`Choose project`}
						</button>
					)}
					{view === 'choose' && searchBox(searchLabel)}
					<MachineChips pane={pane} onPane={setPane} allowAll={view === 'choose'} view={view} onView={setView} />
					{view === 'choose' ? list : sideView}
				</div>
			);
		}

		const title = view === 'add' ? t`Add project` : view === 'manage' ? t`Manage projects` : t`Choose project`;
		const subtitle =
			view === 'add'
				? managed
					? t`On ${managedName}. Konvo only sees folder names, never files.`
					: ''
				: view === 'manage'
					? t`The projects your agent tool knows on each machine.`
					: '';
		return (
			// biome-ignore lint/a11y/noStaticElementInteractions: picker-wide arrow keys and Enter; every row is a button.
			<div className={styles.browser} onKeyDown={onKeyDown} data-flx="konvo-agents.choose-project">
				<header className={clsx(styles.head, view !== 'choose' && styles.headBack)}>
					{view !== 'choose' && (
						<button
							type="button"
							className={styles.iconButton}
							onClick={back}
							aria-label={t`Back`}
							data-flx="konvo-agents.choose-project.back"
						>
							<IconChevronLeft size={20} stroke={1.8} />
						</button>
					)}
					<div className={styles.headText}>
						<h2>{title}</h2>
						{subtitle && <p>{subtitle}</p>}
					</div>
					{onClose && (
						<button type="button" className={styles.iconButton} onClick={onClose} aria-label={t`Close`}>
							<IconX size={20} stroke={1.7} />
						</button>
					)}
				</header>
				<div className={styles.body}>
					<MachinePane
						pane={view === 'choose' ? pane : (managed?.id ?? pane)}
						onPane={setPane}
						allowAll={view === 'choose'}
						view={view}
						onView={setView}
						counts={view === 'choose' ? counts : null}
					/>
					<div className={styles.main}>
						{view === 'choose' ? (
							<>
								<div className={styles.pad}>{searchBox(searchLabel)}</div>
								{paneMachine && (
									<div className={styles.meta}>
										<MachineGlyph platform={paneMachine.platform} size={14} />
										{[
											paneMachine.tools.map((tool) => tool.name).join(', '),
											plural(paneProjectCount, {one: '# project', other: '# projects'}),
										]
											.filter(Boolean)
											.join(' · ')}
										<span className={styles.metaEnd}>
											<OnlineDot online={paneMachine.online} />
											{paneMachine.online ? t`Online` : t`Offline`}
										</span>
									</div>
								)}
								{list}
								<footer className={styles.keys}>
									<span className={styles.keysStart}>
										{startsOn && (
											<>
												<IconArrowRight size={13} stroke={1.8} />
												<Trans>
													Starts on <strong>{startsOn}</strong> as {me} via Konvo
												</Trans>
											</>
										)}
									</span>
									<span>
										<kbd className={styles.kbd}>
											<IconArrowUp size={12} stroke={2} />
										</kbd>
										<kbd className={styles.kbd}>
											<IconArrowDown size={12} stroke={2} />
										</kbd>
										{t`move`}
									</span>
									<span>
										<kbd className={styles.kbd}>
											<IconCornerDownLeft size={12} stroke={2} />
										</kbd>
										{t`choose`}
									</span>
									<span>
										<kbd className={styles.kbd}>
											<IconArrowLeft size={12} stroke={2} />
										</kbd>
										<kbd className={styles.kbd}>
											<IconArrowRight size={12} stroke={2} />
										</kbd>
										{t`machine`}
									</span>
								</footer>
							</>
						) : managed && canManageProjects(managed) ? (
							sideView
						) : (
							<ManageUnavailable machine={managed} />
						)}
					</div>
				</div>
			</div>
		);
	},
);

/** Why Add and Manage can't run on this machine right now. */
export function ManageUnavailable({machine}: {machine: AgentMachineView | null}) {
	const {t} = useLingui();
	const name = machine?.name ?? '';
	return (
		<p className={styles.note} data-flx="konvo-agents.choose-project.unavailable">
			{!machine
				? t`None of your machines allows starting chats from Konvo.`
				: !machine.online
					? t`${name} is offline. Projects can be added and changed while it is online.`
					: t`${name} cannot change its projects from Konvo yet. Its konvo-agents updates itself within the hour, or run konvo-agents update on it.`}
		</p>
	);
}

/** The Send to an agent panel's inline list (chip layout). */
export const AgentProjectList = observer(
	({
		value,
		onPick,
		autoFocus = false,
	}: {
		value?: ProjectChoice | null;
		onPick: (choice: ProjectChoice) => void;
		autoFocus?: boolean;
	}) => <AgentProjectBrowser layout="chips" value={value} onPick={onPick} autoFocus={autoFocus} />,
);

/** The phone's bottom sheet. */
export const AgentProjectSheet = observer(
	({
		isOpen,
		onClose,
		value,
		onPick,
	}: {
		isOpen: boolean;
		onClose: () => void;
		value?: ProjectChoice | null;
		onPick: (choice: ProjectChoice) => void;
	}) => {
		const {t} = useLingui();
		const [view, setView] = useState<ProjectView>('choose');
		useEffect(() => {
			if (!isOpen) setView('choose');
		}, [isOpen]);
		const title = view === 'add' ? t`Add project` : view === 'manage' ? t`Manage projects` : t`Choose project`;
		return (
			<BottomSheet
				isOpen={isOpen}
				onClose={onClose}
				title={title}
				headerBorder={false}
				snapPoints={[0, 0.9]}
				initialSnap={1}
			>
				{isOpen && (
					<AgentProjectBrowser
						layout="chips"
						value={value}
						onViewChange={setView}
						onPick={(choice) => {
							onClose();
							onPick(choice);
						}}
					/>
				)}
			</BottomSheet>
		);
	},
);

/** Desktop: the browser in a fixed-size dialog. */
export function openAgentProjectDialog(
	value: ProjectChoice | null,
	onPick: (choice: ProjectChoice) => void,
	initialView: ProjectView = 'choose',
): void {
	const close = () => ModalCommands.popWithKey(MODAL_KEY);
	ModalCommands.pushWithKey(
		ModalCommands.modal(() => (
			<ProjectDialog value={value} onClose={close} onPick={onPick} initialView={initialView} />
		)),
		MODAL_KEY,
	);
}

function ProjectDialog({
	value,
	onClose,
	onPick,
	initialView,
}: {
	value: ProjectChoice | null;
	onClose: () => void;
	onPick: (choice: ProjectChoice) => void;
	initialView: ProjectView;
}) {
	const {t} = useLingui();
	return (
		<Modal.Root
			size="large"
			centered
			onClose={onClose}
			className={styles.dialog}
			data-flx="konvo-agents.choose-project.dialog"
		>
			<Modal.ScreenReaderLabel text={t`Choose project`} />
			<AgentProjectBrowser
				layout="panes"
				autoFocus
				value={value}
				initialView={initialView}
				onClose={onClose}
				onPick={(choice) => {
					onClose();
					onPick(choice);
				}}
			/>
		</Modal.Root>
	);
}
